import { useState } from 'react';
import useAuth from '@/auth/useAuth';
import { useGetStorageLogsByUserId } from '@/service/storage-log/hook';
import { Button } from '@/components/ui/button';
import { RefreshCwIcon, FileIcon, FolderIcon, CheckCircle2, XCircle, Clock } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import PATH_PAGE from '@/router/path';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import StorageLogSearch, { StorageLogFilterSchema } from './storage-log-search';
import StorageLogDashboard from './storage-log-dashboard';
import { isDateInRange } from '@/utils/date';

const formatBytes = (bytes: number | null) => {
  if (bytes === null || bytes === 0) return '-';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getOperationBadgeVariant = (operation: string) => {
  switch (operation.toLowerCase()) {
    case 'upload':
      return 'default';
    case 'download':
      return 'secondary';
    case 'delete':
      return 'destructive';
    case 'copy':
      return 'outline';
    default:
      return 'secondary';
  }
};

const getStatusIcon = (status: string) => {
  switch (status.toLowerCase()) {
    case 'success':
    case 'completed':
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    case 'failed':
    case 'error':
      return <XCircle className="h-4 w-4 text-red-500" />;
    default:
      return <Clock className="h-4 w-4 text-yellow-500" />;
  }
};

const FilePathDisplay = ({ filePaths }: { filePaths: string[] | null }) => {
  if (!filePaths || filePaths.length === 0) {
    return <span className="text-muted-foreground">-</span>;
  }

  if (filePaths.length === 1) {
    return (
      <div className="flex items-center gap-1.5 max-w-[250px]">
        <FileIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        <span className="truncate text-sm font-mono" title={filePaths[0]}>
          {filePaths[0]}
        </span>
      </div>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-1.5 max-w-[250px] hover:bg-muted/50 rounded px-1 py-0.5 -mx-1 transition-colors">
          <FolderIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          <span className="text-sm font-medium text-primary">
            {filePaths.length} files
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="px-3 py-2 border-b">
          <p className="text-sm font-medium">{filePaths.length} files</p>
        </div>
        <ScrollArea className="max-h-[200px]">
          <div className="p-2 space-y-1">
            {filePaths.map((path, index) => (
              <div
                key={index}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50"
              >
                <FileIcon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                <span className="text-xs font-mono break-all">{path}</span>
              </div>
            ))}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

const ServerlessStorageLogsOverview = () => {
  const { session } = useAuth();
  const { orgId, workspaceId } = useParamWorkspaceId();
  const userId = session?.user?.id;
  const [filter, setFilter] = useState<StorageLogFilterSchema>({
    projectId: '',
    operation: [],
    limit: '100',
  });

  const { data, isLoading, refetch } = useGetStorageLogsByUserId(
    {
      user_id: userId!,
    },
    {
      refetchInterval: 1000 * 60,
    }
  );

  const filteredData = data
    ?.filter((log) => {
      // Filter by project ID
      if (filter.projectId && !log.project_id.toLowerCase().includes(filter.projectId.toLowerCase())) {
        return false;
      }
      // Filter by operation
      if (filter.operation && filter.operation.length > 0 && !filter.operation.includes(log.operation_type.toLowerCase())) {
        return false;
      }
      // Filter by date
      if (!isDateInRange(log.created_at, filter.date)) {
        return false;
      }
      return true;
    })
    .slice(0, parseInt(filter.limit || '100'));

  return (
    <div>
      <div className="container mt-8">
        <div className="mb-4 flex flex-col items-start gap-4">
          <div className="flex items-center justify-between w-full">
            <h2 className="text-lg font-semibold">Storage Logs</h2>
            <Button
              variant="outline"
              onClick={() => refetch()}
              size={'sm'}
              startIcon={<RefreshCwIcon className="h-4 w-4" />}
              disabled={isLoading}
            >
              Refresh
            </Button>
          </div>
          <div className="w-full">
            <StorageLogSearch
              onSearch={(data) => setFilter(data)}
              showProjectFilter={true}
            />
          </div>
        </div>

        {data && data.length > 0 && (
          <StorageLogDashboard data={data} />
        )}

        <h3 className="text-md font-semibold mb-4">Recent Logs</h3>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : !filteredData || filteredData.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <FileIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No storage logs found</p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project</TableHead>
                  <TableHead>Operation</TableHead>
                  <TableHead>File Path</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead className="text-right">Files</TableHead>
                  <TableHead className="text-right">Size</TableHead>
                  <TableHead className="text-right">Duration</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Link
                        to={PATH_PAGE.dashboard.serverlessGPU.byId(orgId, workspaceId, log.project_id).overview}
                        className="text-sm font-mono text-primary hover:underline"
                      >
                        {log.project_id.slice(0, 8)}...
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getOperationBadgeVariant(log.operation_type) as any}>
                        {log.operation_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <FilePathDisplay filePaths={log.file_path} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(log.status)}
                        <span className="text-sm capitalize">{log.status}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm font-mono">
                      {log.client_ip || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {log.file_count ?? '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatBytes(log.file_size_bytes)}
                    </TableCell>
                    <TableCell className="text-right">
                      {log.duration_ms ? `${log.duration_ms}ms` : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(log.created_at), {
                        addSuffix: true,
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServerlessStorageLogsOverview;
